import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import RulesModal from "../components/RulesModal";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    location: "",
    experience_years: "",
  });
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!acceptedRules) {
      setError("Bitte akzeptiere die Community-Regeln.");
      return;
    }
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      location: form.location.trim() || null,
      experience_years: form.experience_years ? parseInt(form.experience_years, 10) : null,
      accepted_rules: true,
    };
    const r = await register(payload);
    setBusy(false);
    if (r.ok) navigate("/chat");
    else setError(r.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center hero-bg p-4 py-10">
      <div className="w-full max-w-md" data-testid="register-page">
        <div className="bg-[#FFFFFF] backdrop-blur-xl border border-slate-200 rounded-2xl p-8 shadow-[0_20px_80px_rgba(0,102,255,0.15)]">
          <div className="flex flex-col items-center text-center mb-6">
            <BrandLogo size={72} className="mb-3" />
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter text-slate-900">
              Werde Teil der Crew 🚗
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Registriere dich und tausche dich mit anderen Fahrzeugüberführern aus.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <Field label="Name" required value={form.name} onChange={set("name")} placeholder="Max Mustermann" testId="reg-name" />
            <Field label="E-Mail" type="email" required value={form.email} onChange={set("email")} placeholder="du@beispiel.de" testId="reg-email" />
            <Field label="Passwort" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="Mind. 6 Zeichen" testId="reg-password" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Standort" value={form.location} onChange={set("location")} placeholder="z. B. Hamburg" testId="reg-location" />
              <Field label="Erfahrung (Jahre)" type="number" min="0" max="80" value={form.experience_years} onChange={set("experience_years")} placeholder="5" testId="reg-experience" />
            </div>

            <button
              type="button"
              onClick={() => setAcceptedRules((s) => !s)}
              data-testid="rules-checkbox-label"
              className={`w-full text-left flex items-start gap-3 p-4 rounded-xl border-2 transition-all ${
                acceptedRules
                  ? "bg-[#1D5BFF]/10 border-[#1D5BFF]"
                  : "bg-[#F5F8FD] border-slate-200 hover:border-slate-300"
              }`}
            >
              <div
                className={`shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${
                  acceptedRules ? "bg-[#1D5BFF] border-[#1D5BFF]" : "bg-[#F5F8FD] border-slate-500"
                }`}
                data-testid="rules-checkbox"
                role="checkbox"
                aria-checked={acceptedRules}
              >
                {acceptedRules && (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8.5L6.5 12L13 4.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-slate-800 leading-relaxed">
                Ich akzeptiere die{" "}
                <span
                  role="link"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setShowRules(true); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setShowRules(true); } }}
                  data-testid="open-rules-btn"
                  className="text-[#4DBFFF] hover:text-slate-900 font-semibold underline underline-offset-2 cursor-pointer"
                >
                  Community-Regeln
                </span>{" "}
                und bestätige, dass ich aktiver Fahrzeugüberführer bin.
              </span>
            </button>

            {error && (
              <div
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit-btn"
              className="btn-brand w-full font-bold py-3 rounded-full flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Konto erstellen <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            Schon registriert?{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-[#4DBFFF] hover:text-[#1D5BFF] font-semibold">
              Zur Anmeldung
            </Link>
          </div>
        </div>
      </div>
      <RulesModal open={showRules} onClose={() => setShowRules(false)} />
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
        {label}
      </label>
      <input
        {...props}
        data-testid={`${testId}-input`}
        className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30 transition-all"
      />
    </div>
  );
}
